import { ReactNode, createContext, useContext } from 'react';

import {
  createPatientHistoryRequest,
  createPatientInformationRequest,
  saveReferralAttachmentRequest,
  saveReferralReasonRequest,
  updatePatientHistoryRequest,
  updatePatientInformationRequest,
} from '@reducers/referral/ReferralAction';
import {
  getAllergiesRequest,
  getSedationsRequest,
  getSpecialConsiderationsRequest,
  getSpecialtiesAndServicesRequest,
} from '@reducers/shared/SharedAction';

/** The request creators the referral steps save and load with. */
const productStepRequests = {
  createPatientInformationRequest,
  updatePatientInformationRequest,
  createPatientHistoryRequest,
  updatePatientHistoryRequest,
  saveReferralReasonRequest,
  saveReferralAttachmentRequest,
  getAllergiesRequest,
  getSpecialConsiderationsRequest,
  getSedationsRequest,
  getSpecialtiesAndServicesRequest,
};

/**
 * The same creators by name. A stand-in keeps the creator's contract: called
 * with the same arguments, it returns a thunk whose result has a `$promise`
 * resolving to `{ data: { data } }`, or rejecting with an axios-style error
 * (`response.status` 422 and `response.data.errors` for field errors).
 */
export type StepRequests = Record<keyof typeof productStepRequests, (...args: any[]) => any>;

export type GuestReferral = {
  /** Stand-ins that keep the referral in the browser until it is submitted. */
  requests: StepRequests;
  /** The consent and captcha shown above the last step's button. */
  submitExtras: ReactNode;
  /** Whether the last step may submit (consent given, captcha done). */
  canSubmit: boolean;
  submitLabel: string;
  /** Explains why the last step may not submit yet. */
  onSubmitBlocked: () => void;
  /** The referral is in: show the confirmation. */
  onSubmitted: (result: any) => void;
  /**
   * A refused submission. Returns the field errors the last step shows
   * itself, or null when the guest page has shown the refusal.
   */
  onSubmitError: (error: any) => Record<string, string[]> | null;
};

const GuestReferralContext = createContext<GuestReferral | null>(null);

export const GuestReferralProvider = GuestReferralContext.Provider;

/**
 * ENH-052 (plan D11): the guest form's hooks, or null in the signed-in form.
 * Under a GuestReferralProvider the referral steps are the product's own, and
 * only where they save, go back and finish differs.
 */
export const useGuestReferral = () => useContext(GuestReferralContext);

/** The product's request creators, or the guest stand-ins under a GuestReferralProvider. */
export const useReferralStepRequests = (): StepRequests => (
  useGuestReferral()?.requests ?? productStepRequests
);
